import React, { useEffect, useState, Suspense, lazy } from "react";
import { useLocation } from "react-router-dom";
import useLocationUpdate from "../hooks/useLocationUpdate";
import TopNavBar from "../components/TopNavBar";
import { useActiveChannel } from "../contexts/ActiveChannelContext";
import { useScroll } from "../contexts/ScrollContext";
import { useAuthStatus, AuthStatus } from "../hooks/useAuthStatus";

const LocationPermissionModal = lazy(() => import("./LocationPermissionModal"));
// const InteractiveChatbot = lazy(() => import("./InteractiveChatbot"));

const Layout = ({ children, isMobile, isAppReady }) => {
  const { status: authStatus } = useAuthStatus();
  const isAuthenticated = authStatus === AuthStatus.AUTHENTICATED;
  const { enableLocationInApp, disableLocationInApp, locationEnabledInApp } =
    useLocationUpdate();
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const { hasActiveChannel } = useActiveChannel();
  const { showHeader } = useScroll();
  const location = useLocation();

  // Synchronized visibility gate
  const showAuthUI = isAuthenticated && isAppReady && !hasActiveChannel;

  // Hide TopNavBar on /home route only on mobile
  const isHomePage = location.pathname === "/home";
  const shouldShowTopNav = !isMobile || !isHomePage; // Hide on /home only on mobile, show everywhere else

  // Track if user has already dismissed the location modal this session
  const [userDismissedModal, setUserDismissedModal] = useState(false);

  useEffect(() => {
    // Show modal only when:
    // 1. User is authenticated and app is ready
    // 2. User hasn't made a choice yet (locationEnabledInApp is null/undefined)
    // 3. User hasn't dismissed the modal this session
    // Note: Do NOT show when locationEnabledInApp === false (user already said no)
    const hasNotDecided =
      locationEnabledInApp === null || locationEnabledInApp === undefined;
    if (showAuthUI && hasNotDecided && !userDismissedModal) {
      setShowPermissionModal(true);
    } else {
      setShowPermissionModal(false);
    }
  }, [showAuthUI, locationEnabledInApp, userDismissedModal]);

  return (
    <div
      className="flex flex-col h-[100dvh] w-full overflow-hidden bg-white text-gray-900"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      <div
        className={`z-[100] ${
          showAuthUI
            ? shouldShowTopNav
              ? "relative shrink-0 opacity-100 pointer-events-auto"
              : "hidden"
            : "absolute inset-x-0 top-0 opacity-0 pointer-events-none"
        }`}
      >
        <TopNavBar />
      </div>

      <main className="flex-1 flex flex-col min-h-0 relative">
        <div className="flex flex-1 min-h-0 w-full md:flex-row relative">
          {children}

          {/* <Suspense fallback={null}>
            <div
              className={`z-50 transition-opacity duration-200 ease-in-out ${
                showAuthUI
                  ? "relative opacity-100 pointer-events-auto"
                  : "absolute inset-x-0 bottom-0 opacity-0 pointer-events-none"
              }`}
            >
              {!isMobile && <InteractiveChatbot />}
            </div>
          </Suspense> */}
        </div>

        {/* Location Permission Modal - rendered outside flow for proper z-index */}
        <Suspense fallback={null}>
          {showPermissionModal && (
            <LocationPermissionModal
              show
              onGrant={async () => {
                if (await enableLocationInApp()) setShowPermissionModal(false);
              }}
              onDeny={async () => {
                await disableLocationInApp();
                setUserDismissedModal(true);
                setShowPermissionModal(false);
              }}
            />
          )}
        </Suspense>
      </main>
    </div>
  );
};

export default Layout;
